'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCustomRouter } from '@/hooks/useCustomRouter';

import {
  parseEnumValue,
  parseNumberList,
  parseStringList,
  toggleArrayItem,
} from '../utils';

export type DonationBusinessSort =
  | 'LATEST'
  | 'AMOUNT_DESC'
  | 'RATE_DESC'
  | 'SUPPORT_DESC'
  | 'DEADLINE_ASC';

const DONATION_SORT_OPTIONS: readonly DonationBusinessSort[] = [
  'LATEST',
  'AMOUNT_DESC',
  'RATE_DESC',
  'SUPPORT_DESC',
  'DEADLINE_ASC',
];

export interface IntegratedSearchFilterState {
  regionCode: string | null;
  subRegionCode: string | null;
  policyInfoProvisionCodes: string[];
  giftCategoryIds: number[];
  giftInstitutionIds: number[];
  donationInstitutionIds: number[];
  donationSort: DonationBusinessSort;
}

const DEFAULTS: IntegratedSearchFilterState = {
  regionCode: null,
  subRegionCode: null,
  policyInfoProvisionCodes: [],
  giftCategoryIds: [],
  giftInstitutionIds: [],
  donationInstitutionIds: [],
  donationSort: 'LATEST',
};

const PARAM_KEYS = {
  regionCode: 'region',
  subRegionCode: 'subRegion',
  policyInfoProvisionCodes: 'guideProvisions',
  giftCategoryIds: 'giftCategories',
  giftInstitutionIds: 'giftInstitutions',
  donationInstitutionIds: 'donationInstitutions',
  donationSort: 'donationSort',
  page: 'page',
} as const;

function parse(params: URLSearchParams): IntegratedSearchFilterState {
  return {
    regionCode: params.get(PARAM_KEYS.regionCode) || null,
    subRegionCode: params.get(PARAM_KEYS.subRegionCode) || null,
    policyInfoProvisionCodes: parseStringList(
      params.get(PARAM_KEYS.policyInfoProvisionCodes),
    ),
    giftCategoryIds: parseNumberList(params.get(PARAM_KEYS.giftCategoryIds)),
    giftInstitutionIds: parseNumberList(
      params.get(PARAM_KEYS.giftInstitutionIds),
    ),
    donationInstitutionIds: parseNumberList(
      params.get(PARAM_KEYS.donationInstitutionIds),
    ),
    donationSort: parseEnumValue(
      params.get(PARAM_KEYS.donationSort),
      DONATION_SORT_OPTIONS,
      DEFAULTS.donationSort,
    ),
  };
}

function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string | undefined,
) {
  if (value) params.set(key, value);
  else params.delete(key);
}

function write(params: URLSearchParams, state: IntegratedSearchFilterState) {
  setOrDelete(params, PARAM_KEYS.regionCode, state.regionCode ?? undefined);
  setOrDelete(
    params,
    PARAM_KEYS.subRegionCode,
    state.subRegionCode ?? undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.policyInfoProvisionCodes,
    state.policyInfoProvisionCodes.length
      ? state.policyInfoProvisionCodes.join(',')
      : undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.giftCategoryIds,
    state.giftCategoryIds.length ? state.giftCategoryIds.join(',') : undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.giftInstitutionIds,
    state.giftInstitutionIds.length
      ? state.giftInstitutionIds.join(',')
      : undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.donationInstitutionIds,
    state.donationInstitutionIds.length
      ? state.donationInstitutionIds.join(',')
      : undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.donationSort,
    state.donationSort !== DEFAULTS.donationSort
      ? state.donationSort
      : undefined,
  );
  // 통합 카테고리 필터 변경은 항상 첫 페이지로 리셋
  params.delete(PARAM_KEYS.page);
}

export function useIntegratedSearchFilter() {
  const router = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const paramsString = searchParams?.toString() ?? '';

  const [state, setState] = useState<IntegratedSearchFilterState>(() =>
    parse(new URLSearchParams(paramsString)),
  );

  useEffect(() => {
    const parsed = parse(new URLSearchParams(paramsString));
    setState((prev) =>
      JSON.stringify(prev) === JSON.stringify(parsed) ? prev : parsed,
    );
  }, [paramsString]);

  const pendingRef = useRef<IntegratedSearchFilterState | null>(null);

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;

    const params = new URLSearchParams(paramsString);
    write(params, pending);
    const qs = params.toString();
    const url = qs ? `${pathname}?${qs}` : pathname;
    router.replace(url, { scroll: false });
  }, [state, pathname, paramsString, router]);

  const update = useCallback(
    (
      updater: (
        prev: IntegratedSearchFilterState,
      ) => IntegratedSearchFilterState,
    ) => {
      setState((prev) => {
        const next = updater(prev);
        pendingRef.current = next;

        return next;
      });
    },
    [],
  );

  const setRegionCode = useCallback(
    (code: string | null) => {
      update((prev) =>
        prev.regionCode === code
          ? prev
          : { ...prev, regionCode: code, subRegionCode: null },
      );
    },
    [update],
  );

  const setSubRegionCode = useCallback(
    (code: string | null) => {
      update((prev) => ({ ...prev, subRegionCode: code }));
    },
    [update],
  );

  const togglePolicyInfoProvision = useCallback(
    (code: string) => {
      update((prev) => ({
        ...prev,
        policyInfoProvisionCodes: toggleArrayItem(
          prev.policyInfoProvisionCodes,
          code,
        ),
      }));
    },
    [update],
  );

  const toggleGiftCategory = useCallback(
    (id: number) => {
      update((prev) => ({
        ...prev,
        giftCategoryIds: toggleArrayItem(prev.giftCategoryIds, id),
      }));
    },
    [update],
  );

  const toggleGiftInstitution = useCallback(
    (id: number) => {
      update((prev) => ({
        ...prev,
        giftInstitutionIds: toggleArrayItem(prev.giftInstitutionIds, id),
      }));
    },
    [update],
  );

  const toggleDonationInstitution = useCallback(
    (id: number) => {
      update((prev) => ({
        ...prev,
        donationInstitutionIds: toggleArrayItem(
          prev.donationInstitutionIds,
          id,
        ),
      }));
    },
    [update],
  );

  const setDonationSort = useCallback(
    (sort: DonationBusinessSort) => {
      update((prev) => ({ ...prev, donationSort: sort }));
    },
    [update],
  );

  const resetHometownNewsFilters = useCallback(() => {
    update((prev) => ({
      ...prev,
      regionCode: null,
      subRegionCode: null,
    }));
  }, [update]);

  const resetPolicyInfoFilters = useCallback(() => {
    update((prev) => ({ ...prev, policyInfoProvisionCodes: [] }));
  }, [update]);

  const resetGiftFilters = useCallback(() => {
    update((prev) => ({
      ...prev,
      giftCategoryIds: [],
      giftInstitutionIds: [],
    }));
  }, [update]);

  const resetDonationFilters = useCallback(() => {
    update((prev) => ({ ...prev, donationInstitutionIds: [] }));
  }, [update]);

  const hometownNewsFilterCount =
    (state.regionCode ? 1 : 0) + (state.subRegionCode ? 1 : 0);
  const policyInfoFilterCount = state.policyInfoProvisionCodes.length;
  const giftFilterCount =
    state.giftCategoryIds.length + state.giftInstitutionIds.length;
  const donationFilterCount = state.donationInstitutionIds.length;

  return {
    state,
    setRegionCode,
    setSubRegionCode,
    togglePolicyInfoProvision,
    toggleGiftCategory,
    toggleGiftInstitution,
    toggleDonationInstitution,
    setDonationSort,
    resetHometownNewsFilters,
    resetPolicyInfoFilters,
    resetGiftFilters,
    resetDonationFilters,
    hometownNewsFilterCount,
    policyInfoFilterCount,
    giftFilterCount,
    donationFilterCount,
  };
}
